// ─────────────────────────────────────────────────────────────────────────────
// Per-user identity colors
//
// This is SEPARATE from the company-wide accent color (company.accentColor,
// "Brand Accent Color" in Settings) which drives UI chrome like active pipeline
// stages. A userColor is a personal identity color used for a person's avatar
// circle, task assignments, and anywhere their name/initials appear.
//
// Palette philosophy: muted "field notes" mid-tones that read clearly as a
// white-on-color avatar in BOTH day and night mode. None of them sit near
// #d4a017 (Imperial Gold — the default company accent) so the two systems are
// never visually confused.
//
// NOTE: the 12 base colors + the hash in colorForUid() are mirrored server-side
// in functions/index.js (createCompanyUser). Keep them in sync so a color
// assigned at creation matches the client-side default fallback.
// ─────────────────────────────────────────────────────────────────────────────

// 12 canonical identity colors. The hash in colorForUid() indexes into this set,
// so the ordering is stable — do not reorder without understanding that it
// changes every existing default assignment.
export const USER_COLOR_PALETTE = [
  { hex: '#5b6c8f', name: 'Slate Blue' },
  { hex: '#b5654a', name: 'Terracotta' },
  { hex: '#5f7d5f', name: 'Sage' },
  { hex: '#b06b7a', name: 'Dusty Rose' },
  { hex: '#7d5ba6', name: 'Warm Purple' },
  { hex: '#2f7d8a', name: 'Ocean Teal' },
  { hex: '#c2642f', name: 'Burnt Orange' },
  { hex: '#4a6b8a', name: 'Steel Blue' },
  { hex: '#80803f', name: 'Olive' },
  { hex: '#8a3d4f', name: 'Burgundy' },
  { hex: '#a9663a', name: 'Copper' },
  { hex: '#2d6a4f', name: 'Forest' },
];

// ── Picker palette ───────────────────────────────────────────────────────────
// Deliberately chosen, distinct, professional avatar colors (bold-but-not-cheesy
// — think a well-designed productivity app). This is SEPARATE from
// USER_COLOR_PALETTE above, which only seeds hashed defaults; the two need not
// match. Hex strings (not {hex,name}) so the picker maps them directly.

// Row 1 — the eight shown the moment the picker is revealed.
export const INITIAL_USER_COLORS = [
  '#C0392B', // Crimson
  '#E67E22', // Burnt Orange
  '#D4AC0D', // Amber (darker)
  '#27AE60', // Emerald
  '#1ABC9C', // Teal
  '#2980B9', // Ocean Blue
  '#8E44AD', // Purple
  '#2C3E50', // Slate
];

// Remaining colors revealed under "Show more" (scrollable area). Shown all at
// once — no further paging.
export const EXTENDED_USER_COLORS = [
  '#E74C3C', // Red
  '#E91E63', // Pink
  '#FF5722', // Deep Orange
  '#FF9800', // Orange
  '#A8B400', // Lime (darker)
  '#4CAF50', // Green
  '#009688', // Teal Dark
  '#00BCD4', // Cyan
  '#3F51B5', // Indigo
  '#9C27B0', // Deep Purple
  '#795548', // Brown
  '#607D8B', // Blue Grey
  '#B7950B', // Dark Gold
  '#CB4335', // Dark Red
  '#1F618D', // Dark Blue
  '#117A65', // Dark Teal
  '#6C3483', // Dark Purple
  '#784212', // Dark Brown
  '#1B2631', // Near Black
  '#717D7E', // Steel Grey
];

// ── Per-user ACCENT color palette ───────────────────────────────────────────
// A user's personal accent (My Profile › Appearance) drives UI chrome — active
// pipeline stage indicators, kanban headers, active type chips — wherever the
// company-wide Company Accent Color would otherwise apply. The accent is a
// TWO-COLOR system: a background fill (accentColor) plus a text color rendered
// on that fill (accentTextColor, default warm off-white #f4f1eb).
//
// Row 1 — the twelve primary swatches shown the moment the picker is
// revealed. Rich, dark, regal tones (deep jewel colors and aged metals, never
// bright) — each reads as a deliberate, confident brand choice, led by Rich
// Imperial Gold, the BoneFlow signature. Near Black is a first-class option:
// a near-black accent is bold, not an absence of choice.
export const INITIAL_ACCENT_COLORS = [
  '#b8860b', // Rich Imperial Gold (deep, aged, not bright)
  '#0a5c30', // Deep Emerald (jewel green, dark)
  '#0a2060', // Midnight Cobalt (deep navy blue)
  '#6b0a15', // Deep Crimson (dark wine red)
  '#3a0a5c', // Royal Purple (dark, regal)
  '#8b3a0a', // Burnished Copper (rich, industrial)
  '#0a3a3a', // Ink Teal (dark and distinctive)
  '#1a1a1a', // Near Black (bold, confident)
  '#4a0a0a', // Dark Burgundy (deep, elegant)
  '#0a1a3a', // Deep Steel (almost black blue)
  '#2a0a3a', // Dark Violet (deep and refined)
  '#3a1a0a', // Dark Amber (deep warm brown)
];

// Remaining accent colors revealed under "Show more" — even deeper variants
// of the primary twelve above, plus a row of classic strong dark tones
// (added 2026-07-18) that are brighter than the regal deep set but still
// clearly dark accents.
export const EXTENDED_ACCENT_COLORS = [
  '#8b6508', // Antique Gold
  '#063d20', // Forest Dark
  '#060f3a', // Deep Midnight
  '#4a0710', // Deep Wine
  '#25063d', // Deep Royal
  '#5c2508', // Dark Bronze
  '#062626', // Deep Ink
  '#0d0d0d', // Near Black variant
  '#300707', // Dark Garnet
  '#060d1f', // Abyss Blue
  '#1a0625', // Dark Plum
  '#251006', // Dark Umber
  '#8B0000', // Dark Red / Deep Crimson
  '#006400', // Dark Green
  '#00008B', // Dark Blue
  '#8B008B', // Dark Magenta
  '#FF8C00', // Dark Orange
  '#008B8B', // Dark Cyan
  '#4B0082', // Indigo
  '#800000', // Maroon
  '#556B2F', // Dark Olive Green
  '#8B4513', // Saddle Brown
];

// ── Trailing (past-stage) color palette ─────────────────────────────────────
// A user's trailing color fills PAST/COMPLETED stages on pipeline visuals
// (StageIndicator, the Workflows kanban header pipeline) and empty stages on
// the Dashboard pipeline. Deliberately a small set of dark, muted tones —
// trailing color is a quiet, secondary preference, so light text (#f4f1eb)
// must stay readable on every option. Default: #4a4a4a (the color previously
// hardcoded for past stages).
export const DEFAULT_TRAILING_COLOR = '#4a4a4a';
export const TRAILING_COLORS = [
  '#4a4a4a', // Near Dark Gray (default)
  '#2d2d2d', // Darker Gray
  '#1a1a1a', // Near Black
  '#3a2a2a', // Dark Warm Gray
  '#2a3a2a', // Dark Green Gray
  '#2a2a3a', // Dark Blue Gray
  '#3a2a3a', // Dark Purple Gray
  '#2a3a3a', // Dark Teal Gray
];

// Text color palette — the ONLY colors ever offered for text rendered on an
// accent fill. Exactly TWO options: text is either dark or light, nothing in
// between — the accent background carries all the color, and grays only
// muddied the pairing. #1a1a1a (Near Black) is the BoneFlow primary and the
// default; #f4f1eb (Off White, the BoneFlow warm white) is the light option.
export const ACCENT_TEXT_COLORS = [
  '#1a1a1a', // Near Black (BoneFlow primary, default)
  '#f4f1eb', // Off White (BoneFlow warm white)
];

// Neutral fallback used only when there is no identity at all to derive a color
// from (e.g. a brief loading window before a user record resolves).
export const NEUTRAL_USER_COLOR = '#888';

// Deterministic default color from a uid. Same uid → same color, and the spread
// across 12 buckets makes shared colors within one company unlikely. Used both
// to seed userColor at account creation and as the live display default for any
// user whose userColor hasn't been persisted yet.
export function colorForUid(uid) {
  if (!uid || typeof uid !== 'string') return NEUTRAL_USER_COLOR;
  let h = 0;
  for (let i = 0; i < uid.length; i++) {
    h = (h * 31 + uid.charCodeAt(i)) >>> 0;
  }
  return USER_COLOR_PALETTE[h % USER_COLOR_PALETTE.length].hex;
}

// Resolve the color to render for a user/member object. Prefers the persisted
// userColor; otherwise falls back to the deterministic uid default so avatars
// are always distinct and colored — never a flat grey — for any real user.
// The neutral grey only appears when there's no identity to key on at all.
export function getUserColor(userLike) {
  if (userLike && typeof userLike.userColor === 'string' && userLike.userColor) {
    return userLike.userColor;
  }
  const uid = userLike && (userLike.uid || userLike.id);
  return uid ? colorForUid(uid) : NEUTRAL_USER_COLOR;
}
